import React, { useState } from "react";
import { Info, ChevronDown } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../components/common/Button";
import useUserEmail from "../../utils/useUserEmail";
import useLinkedInAccounts from "../../utils/useLinkedInAccounts";
import ProgressBar from "./ProgressBar";

import logo from "../../assets/sideBar/logo-feelim.svg";
import hamburger from "../../assets/sideBar/menu.svg";
import circle from "../../assets/navbar/green-circle.svg";
import plug from "../../assets/navbar/connect-plug.svg";
import settings from "../../assets/navbar/settings.svg";
import backToHome from "../../assets/navbar/BackToHome.svg";
import dateFilter from "../../assets/navbar/dateFilter.svg";
import filter from "../../assets/navbar/filter.svg";
import redCircle from "../../assets/navbar/red-circle.svg";
import disconnectPlug from "../../assets/navbar/disconnect-plug.svg";
import publishIcon from "../../assets/navbar/publish.svg";

const Navbar = ({ 
  setIsSidebarOpen,
  isSidebarOpen,
  onOpenAccountModal,
  onPublish,
  canPublish,
  showPublish = false,
  progressStage = null,
}) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isCreatePage = location.pathname === "/create";
  const isDraftPage = location.pathname === "/draft";
  const isDashboardPage = location.pathname === "/dashboard";
  const isViewingPage = location.pathname === "/viewing";
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState("This year");
  const [selectedType, setSelectedType] = useState("Filter by types");
  const email = useUserEmail();
  const { isConnected: isLinkedInConnected, status } = useLinkedInAccounts(email);

  return (
    <header
      className="flex items-center justify-between p-4 bg-white shadow-sm h-[80px] sticky top-0 z-40"
    >
      <div className="md:hidden flex items-center justify-between w-full">
        <img src={logo} alt="logo" className="h-8" />
        <button
          onClick={() => setIsSidebarOpen(true)}
        >
          <img src={hamburger} alt="menu" />
        </button>
      </div>
      {/* LEFT: status button + LinkedIn dropdown */}
      <div className="hidden md:flex items-center space-x-2 ml-[123px] flex-1 min-w-0 mr-6">
        {isCreatePage && progressStage && (
          <div className="w-full max-w-[720px]">
             <ProgressBar variant="header" stage={progressStage} />
          </div>
        )}
        {isDashboardPage && (
          <Link to="/create">
            <Button variant="ghost" className="space-x-1">
              <img src={backToHome} alt="backToHome" />
              <span>{t("buttons.backHome")}</span>
            </Button>
          </Link>
        )}
      </div>
      {/* RIGHT: save + publish (create), filters (dashboard) */}
      <div className="hidden md:flex items-center space-x-2">
        {status !== "loading" && (
          <Button
            variant="social"
            onClick={!isLinkedInConnected ? onOpenAccountModal : undefined}
          >
            <img src={isLinkedInConnected ? circle : redCircle} alt="status" />
            <span>{isLinkedInConnected ? t("buttons.connected") : t("buttons.notConnected")}</span>
            <img src={isLinkedInConnected ? plug : disconnectPlug} alt="plug" />
          </Button>
        )}

        {((isCreatePage && showPublish) || isViewingPage) && (
          <Button variant="primary" onClick={onPublish} disabled={!isLinkedInConnected || !canPublish} className="gap-2">
            <span>{t("navbar.publish")}</span>
            <img src={publishIcon} alt="publish" className="w-5 h-5" />
          </Button>
        )}

        {isDashboardPage && (
          <div className="relative flex gap-2">
            <div className="relative">
              <button
                onClick={() => setIsYearDropdownOpen((prev) => !prev)}
                className="flex items-center justify-between px-4 py-2 border border-black-800 rounded text-lg text-black-600 hover:bg-black-10 transition-colors duration-200 w-[240px] h-[46px]"
              >
                <span>{selectedYear}</span>
                <img src={dateFilter} alt="dateFilter" />
              </button>

              {isYearDropdownOpen && (
                <div className="absolute right-0 mt-2 w-[240px] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <ul className="py-2 text-sm text-gray-700">
                    {["This year", "Last year", "Last 3 years"].map((year) => (
                      <li key={year}>
                        <button
                          onClick={() => {
                            setSelectedYear(year);
                            setIsYearDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                        >
                          {year}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setIsTypeDropdownOpen((prev) => !prev)}
                className="flex items-center justify-between px-4 py-2 border border-black-800 rounded text-lg text-black-600 hover:bg-black-10 transition-colors duration-200 w-[240px] h-[46px]"
              >
                <span className="text-black-50">{selectedType}</span>
                <img src={filter} alt="filter" />
              </button>

              {isTypeDropdownOpen && (
                <div className="absolute right-0 mt-2 w-[240px] bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <ul className="py-2 text-sm text-gray-700">
                    {["Type A", "Type B", "Type C"].map((type) => (
                      <li key={type}>
                        <button
                          onClick={() => {
                            setSelectedType(type);
                            setIsTypeDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                        >
                          {type}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
